import React from 'react';
import PipelineAPI from '../../../api/pipeline';
import { formatValidationError } from '../../../api/httpError';
import {
  ReviewCorrection,
  ReviewCorrectionScope,
  ReviewLessonDistillation,
  ReviewVerdict,
} from '../../../api/structs';
import VerdictLessonEditor, { LESSON_MAX_CHARS, lessonTooLong } from './VerdictLessonEditor';

type CorrectionLabel = ReviewCorrection['label'];

export interface VerdictCorrectionBody {
  label: CorrectionLabel;
  reason: string;
  /** The distilled rule; absent when the operator saved without distilling. */
  lesson?: string;
  scope?: ReviewCorrectionScope;
  reinforces_id?: string;
  /**
   * NOT part of the correction payload — the repair the operator ticked, which
   * the panel performs through its own endpoint once the correction is saved.
   */
  dropClosingFrame?: boolean;
}

interface VerdictCorrectionFormProps {
  verdict: ReviewVerdict;
  /** The correction is in flight — everything locks until it lands. */
  busy?: boolean;
  /**
   * What the distil call is addressed by. Without a template id there is no
   * lesson button at all, and the form is exactly what it was before.
   */
  runId?: string;
  templateId?: string;
  checkpointId?: string;
  /** Whether this surface can actually drop the scene's closing frame. */
  canDropClosingFrame?: boolean;
  /**
   * `<provider>/<model>` of the corrected checkpoint's generator, when it
   * declares one. A lesson can be scoped to that model — the rule then reaches
   * every judge whose checkpoint uses it, in any pipeline — and the option is
   * offered only where there is a model to name.
   */
  modelId?: string;
  onSubmit: (body: VerdictCorrectionBody) => Promise<void> | void;
  onCancel: () => void;
}

const OPTIONS: { label: CorrectionLabel; text: string }[] = [
  { label: 'false_pass', text: 'It should have failed' },
  { label: 'false_fail', text: 'It should have passed' },
  { label: 'correct', text: 'The verdict was right' },
];

/**
 * A human's override of one reviewer verdict, logged for judge-kit training.
 * It opens on the complaint that fits the verdict — a pass the operator
 * clicked "Wrong verdict" on was, presumably, a false pass — and insists on a
 * reason for anything but "the verdict was right": a label with no "why"
 * teaches the judge kit nothing.
 *
 * The note alone teaches it little more, though: "the jacket is the wrong
 * colour" is pasted into every later judge prompt with none of the frame that
 * made it legible. So the note can be DISTILLED — one judge call — into a rule
 * that stands on its own and says how far it reaches. That is a deliberate
 * button, not part of saving: it costs credits, it can fail, and a correction
 * saved without it is still a correction.
 */
const VerdictCorrectionForm: React.FC<VerdictCorrectionFormProps> = ({
  verdict,
  busy,
  runId,
  templateId,
  checkpointId,
  canDropClosingFrame,
  modelId,
  onSubmit,
  onCancel,
}) => {
  const [label, setLabel] = React.useState<CorrectionLabel>(
    verdict.pass ? 'false_pass' : 'false_fail'
  );
  const [reason, setReason] = React.useState('');
  const [distilling, setDistilling] = React.useState(false);
  const [distillError, setDistillError] = React.useState('');
  const [distilled, setDistilled] = React.useState<ReviewLessonDistillation | null>(null);
  /**
   * The complaint the distillation was derived FROM. A lesson is a reading of
   * one note; edit the note (or change the label) and everything the judge
   * inferred from it — the reinforcement it matched, the repair it suggested —
   * was inferred about a complaint that no longer exists.
   */
  const [distilledFrom, setDistilledFrom] = React.useState<{
    label: CorrectionLabel;
    reason: string;
  } | null>(null);
  const [lesson, setLesson] = React.useState('');
  const [scope, setScope] = React.useState<ReviewCorrectionScope>('this_pipeline');
  const [separate, setSeparate] = React.useState(false);
  const [reinforcedText, setReinforcedText] = React.useState('');
  const [dropClosingFrame, setDropClosingFrame] = React.useState(false);

  const trimmedReason = reason.trim();
  /**
   * The lesson on screen no longer describes what is about to be saved. It is
   * NOT thrown away — the operator's edits are theirs, and "save the lesson as
   * edited" is a legitimate answer — but everything the judge derived from the
   * old note is dropped: no reinforcement of a lesson matched against a
   * different complaint, and no repair suggested for one.
   */
  const stale = Boolean(
    distilled && distilledFrom && (distilledFrom.label !== label || distilledFrom.reason !== trimmedReason)
  );
  // A lesson over the cap is refused rather than truncated: the whole lessons
  // block is capped before the judge sees it, and half a rule reads as a rule.
  const overLongLesson = Boolean(distilled) && lessonTooLong(lesson);
  const canSubmit =
    !busy && !distilling && !overLongLesson && (label === 'correct' || trimmedReason.length > 0);
  const canDistil = Boolean(templateId && verdict.id) && trimmedReason.length > 0 && !busy;

  /**
   * The distil response names the lesson it reinforces by id only, so its TEXT
   * is read from the template's own corrections. A failed lookup is silent on
   * purpose: the reinforce line still says what is happening, it just cannot
   * quote the rule — and so is a MISS, which this read is bounded into: it sees
   * the default page (the 50 newest corrections), which is where a lesson the
   * judge just matched will be in every realistic case, and is not worth paging
   * the whole history for a quotation.
   */
  const loadReinforcedText = async (id: string) => {
    if (!templateId) return;
    try {
      const data = await PipelineAPI.getReviewCorrections(templateId);
      const match = (data?.corrections || []).find((correction) => correction.id === id);
      setReinforcedText((match?.lesson || match?.reason || '').trim());
    } catch {
      setReinforcedText('');
    }
  };

  const handleDistil = async () => {
    if (!canDistil || !templateId) return;
    setDistilling(true);
    setDistillError('');
    setReinforcedText('');
    try {
      const result = await PipelineAPI.distillReviewLesson(templateId, {
        run_id: runId || '',
        verdict_id: verdict.id || '',
        checkpoint_id: checkpointId || '',
        label,
        reason: trimmedReason,
      });
      setDistilled(result);
      setDistilledFrom({ label, reason: trimmedReason });
      setLesson(result.lesson);
      setScope(result.scope);
      setSeparate(false);
      setDropClosingFrame(false);
      if (result.reinforces_id) void loadReinforcedText(result.reinforces_id);
    } catch (err) {
      setDistillError(formatValidationError(err, 'Could not distil a lesson from that note.'));
    } finally {
      setDistilling(false);
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    const body: VerdictCorrectionBody = { label, reason: trimmedReason };
    const trimmedLesson = lesson.trim();
    // No distillation (or one edited away to nothing) means the old payload
    // exactly: the backend then shows the judge the raw note, as it always has.
    if (distilled && trimmedLesson) {
      body.lesson = trimmedLesson;
      body.scope = scope;
      if (!stale && distilled.reinforces_id && !separate) body.reinforces_id = distilled.reinforces_id;
    }
    if (dropClosingFrame && !stale) body.dropClosingFrame = true;
    void onSubmit(body);
  };

  return (
    <form
      data-testid="verdict-correction-form"
      className="space-y-1.5 border border-white/10 bg-black/40 p-2"
      onSubmit={handleSubmit}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          onCancel();
        }
      }}
    >
      <p className="text-[10px] uppercase tracking-wide text-zinc-500">What did it get wrong?</p>

      <div className="space-y-0.5">
        {OPTIONS.map((option) => (
          <label key={option.label} className="flex items-center gap-1.5 text-[11px] text-zinc-300">
            <input
              type="radio"
              name="verdict-correction-label"
              value={option.label}
              checked={label === option.label}
              disabled={busy}
              onChange={() => setLabel(option.label)}
            />
            <span>{option.text}</span>
          </label>
        ))}
      </div>

      <input
        type="text"
        aria-label="Correction reason"
        placeholder={
          label === 'correct' ? 'Anything worth noting (optional)' : 'Why — what did it miss?'
        }
        maxLength={500}
        disabled={busy}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        className="w-full border border-white/10 bg-black/60 px-1.5 py-1 text-[11px] text-zinc-200 placeholder:text-zinc-600"
      />

      {templateId && (
        <div className="space-y-1">
          <button
            type="button"
            className="btn btn-sm btn-secondary text-[10px]"
            data-testid="verdict-distil-lesson"
            disabled={!canDistil || distilling}
            title={
              canDistil
                ? 'Turn this note into a rule the reviewer can apply on its own'
                : 'Say what it got wrong first'
            }
            onClick={() => void handleDistil()}
          >
            {distilling ? 'Distilling…' : distilled ? 'Distil again' : 'Distil lesson'}
          </button>
          {distillError && (
            <p className="text-[10px] text-red-300" data-testid="verdict-distil-error">
              {distillError}
            </p>
          )}
        </div>
      )}

      {distilled && (
        <VerdictLessonEditor
          lesson={lesson}
          onLessonChange={setLesson}
          scope={scope}
          onScopeChange={setScope}
          modelId={modelId || ''}
          stale={stale}
          reinforcesId={stale ? '' : distilled.reinforces_id}
          reinforcedText={reinforcedText}
          separate={separate}
          onSeparateChange={setSeparate}
          suggestedAction={stale ? '' : distilled.suggested_action}
          canDropClosingFrame={Boolean(canDropClosingFrame)}
          dropClosingFrame={dropClosingFrame}
          onDropClosingFrameChange={setDropClosingFrame}
          disabled={busy || distilling}
        />
      )}

      <div className="flex items-center gap-1.5">
        <button
          type="submit"
          className="btn btn-sm btn-primary text-[10px]"
          disabled={!canSubmit}
          title={
            overLongLesson ? `Trim the lesson to ${LESSON_MAX_CHARS} characters first` : undefined
          }
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          className="btn btn-sm btn-ghost text-[10px]"
          disabled={busy}
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default VerdictCorrectionForm;
